import { createContext, useContext, type ReactNode } from 'react'

const Container = createContext<HTMLElement | null>(null)

/**
 * Where a portalled surface opened inside a dialog should render.
 *
 * A dialog locks the page's scrolling while it is open, and the lock cancels a
 * wheel event whose target is not inside it. A popover portalled to `<body>` is
 * a sibling of the dialog, not a descendant — so a list inside one could be
 * dragged by its scrollbar and reached by the keyboard, but the wheel did
 * nothing. Rendering into the dialog puts it back inside the lock.
 *
 * Only the surfaces that carry their own scroll need it, and only the ones
 * Radix does not already treat as modal: `Select` and `Menu` bring their own
 * lock, which takes over from the dialog's.
 */
export function PortalContainerScope({
  container,
  children,
}: {
  container: HTMLElement | null
  children?: ReactNode
}) {
  return <Container.Provider value={container}>{children}</Container.Provider>
}

/** The element to portal into, or `undefined` for `<body>` — which is what
 *  every surface outside a dialog wants. */
export function usePortalContainer(): HTMLElement | undefined {
  return useContext(Container) ?? undefined
}
